import React, { useState } from "react";
import "./CollectionReport.css";
import {
  FaUserMd,
  FaCalendarAlt,
  FaWallet
} from "react-icons/fa";
import { Pagination } from "@mui/material";
import { dateFormatter } from "../../../../CommonComponents/Utils/DateTimeFormatter";

const DoctorDaywiseCollection = ({ data = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const formatReadableDate = (dateStr) => {
    if (!dateStr || dateStr === "N/A") return "N/A";
    return dateFormatter(dateStr);
  };

  if (!data || data.length === 0) {
    return <div className="no-data">No doctor daywise collection data available for the selected range.</div>;
  }

  // Calculate each doctor's total collection across the selected date range
  const doctorTotalsMap = {};
  data.forEach((item) => {
    const doc = item.doctor || "N/A";
    const amt = item.totalCollected || 0;
    if (amt !== 0) {
      doctorTotalsMap[doc] = (doctorTotalsMap[doc] || 0) + amt;
    }
  });

  const doctorTotals = Object.entries(doctorTotalsMap).map(([doctor, total]) => ({
    doctor,
    total
  }));

  // Group raw doctor records by date so date appears ONCE with vertical rowSpan
  const groupedDataMap = {};
  data.forEach((item) => {
    const amt = item.totalCollected || 0;
    if (amt === 0) return; // Hide doctor if collection is 0

    const d = item.date || "N/A";
    if (!groupedDataMap[d]) {
      groupedDataMap[d] = {
        date: d,
        doctors: [],
        dayTotal: 0
      };
    }
    groupedDataMap[d].doctors.push({
      doctor: item.doctor || "N/A",
      totalCollected: amt
    });
    groupedDataMap[d].dayTotal += amt;
  });

  const groupedDays = Object.values(groupedDataMap).filter((d) => d.doctors.length > 0);

  const totalPages = Math.ceil(groupedDays.length / itemsPerPage) || 1;
  const paginatedDays = groupedDays.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="doctor-daywise-container">
      {/* Top Banner KPI */}
      <div className="daywise-summary-banner">
        <div className="summary-banner-card">
          <div className="banner-icon-box">
            <FaUserMd size={20} color="#2563eb" />
          </div>
          <div className="banner-details">
            <span className="banner-subtitle">DOCTOR TOTAL COLLECTIONS (RANGE)</span>
            <div className="doctor-totals-pills">
              {doctorTotals.map((doc, idx) => (
                <div key={idx} className="doc-range-total-pill">
                  <span className="doc-range-name">{doc.doctor}:</span>
                  <strong className="doc-range-val">
                    ₹ {doc.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </strong>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="summary-banner-meta">
          <span className="meta-pill">
            <FaCalendarAlt size={12} /> {groupedDays.length} Days Listed
          </span>
        </div>
      </div>

      {/* Doctor Daywise Spanned Table */}
      <div className="transactions-section">
        <div className="transactions-section-header">
          <div className="header-title-group">
            <FaUserMd size={16} color="#2563eb" />
            <h3 className="table-title">Doctor Daywise Breakdown</h3>
          </div>
          <span className="count-pill-badge">{groupedDays.length} Days Listed</span>
        </div>

        <div className="table-responsive">
          <table className="report-data-table doctor-spanned-table">
            <thead>
              <tr>
                <th style={{ width: "170px" }}>
                  <div className="th-flex">
                    <FaCalendarAlt size={11} color="#64748b" />
                    <span>DATE</span>
                  </div>
                </th>
                <th>
                  <div className="th-flex">
                    <FaUserMd size={11} color="#64748b" />
                    <span>DOCTOR</span>
                  </div>
                </th>
                <th className="text-right" style={{ width: "220px" }}>
                  <div className="th-flex right-justify">
                    <FaWallet size={11} color="#64748b" />
                    <span>COLLECTION</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedDays.map((dayRow, dayIdx) => {
                return dayRow.doctors.map((doc, docIdx) => (
                  <tr
                    key={`${dayIdx}-${docIdx}`}
                    className={`${docIdx === 0 ? "date-group-first-row" : "sub-doctor-row"}`}
                  >
                    {docIdx === 0 && (
                      <td
                        rowSpan={dayRow.doctors.length}
                        className="date-span-cell"
                      >
                        <div className="date-span-content">
                          <span className="date-pill-tag">{formatReadableDate(dayRow.date)}</span>
                        </div>
                      </td>
                    )}
                    <td>
                      <div className="doctor-user-cell">
                        <span className="doc-avatar-circle">
                          <FaUserMd size={12} color="#2563eb" />
                        </span>
                        <strong className="doc-name-text">{doc.doctor}</strong>
                      </div>
                    </td>
                    <td className="text-right">
                      <span className="amount-val paid-val">
                        ₹ {(doc.totalCollected || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="table-pagination-footer">
          <span className="pagination-info-text">
            Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to{" "}
            <strong>{Math.min(currentPage * itemsPerPage, groupedDays.length)}</strong> of{" "}
            <strong>{groupedDays.length}</strong> days
          </span>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            variant="outlined"
            shape="rounded"
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

export default DoctorDaywiseCollection;
